import React, { forwardRef, useEffect, useState } from "react";
import {
    Container,
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
    Grid,
} from "@mui/material";
import {
    PieChart,
    Pie,
    Tooltip,
    Legend,
    Cell,
    ResponsiveContainer,
} from "recharts";

// This is the key we used in the button component
const storageKey = "inventoryLogReportData";

// --- Reusable Log Table Component ---
// We create this to avoid repeating code for each log type
const LogTable = ({ title, logs, pieColors }) => {
    const theme = useTheme();
    return (
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {title}
            </Typography>
            <TableContainer>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Item Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Log Type</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qty Change</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Cost (Unit)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Value</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {logs.map((log) => (
                            <TableRow
                                key={log._id}
                                sx={{
                                    '&:nth-of-type(odd)': {
                                        backgroundColor: theme.palette.action.hover,
                                    },
                                    '&:hover': { 
                                        backgroundColor: theme.palette.action.selected 
                                    }
                                }}
                            >
                                <TableCell>{new Date(log.dateTime).toLocaleString()}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{log.itemName}</TableCell>
                                <TableCell>
                                    <Box component="span" sx={{
                                        fontWeight: 'bold',
                                        color: pieColors[log.logType] || 'text.primary'
                                    }}>
                                        {log.logType}
                                    </Box>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', color: log.quantityChange < 0 ? 'error.main' : 'success.main' }}>
                                    {log.quantityChange}
                                </TableCell>
                                <TableCell align="right">${log.costUsed.toFixed(2)}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                    ${log.totalValue.toFixed(2)}
                                </TableCell>
                                <TableCell>{log.notes}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

/**
 * Processes raw log and item data into a structured report.
 */
const processLogReportData = (items, logs) => {
    // ALWAYS initialize the full return structure
    const result = {
        kpis: {
            totalValueIn: 0,
            totalValueOut: 0,
            totalAdjustments: 0,
            totalSpoilage: 0,
        },
        charts: {
            pieChartData: [],
        },
        groupedReport: {}, // This will hold the separated log tables
        totalLogCount: 0,
    };

    // If no items or logs, return the initialized (zeroed) structure
    if (!items || !logs || (Array.isArray(items) && items.length === 0)) {
        return result;
    }

    const itemMap = new Map();
    items.forEach(item => {
        itemMap.set(item._id, { name: item.item, cost: item.pCost || 0 });
    });

    const chartData_byTypeCount = {};
    const logTypes = Object.keys(logs);

    logTypes.forEach(logType => {
        if (Array.isArray(logs[logType])) {
            logs[logType].forEach(log => {
                result.totalLogCount++;
                const itemInfo = itemMap.get(log.inventoryItemID) || { name: 'Unknown Item', cost: 0 };

                const cost = (log.unitCost > 0 ? log.unitCost : itemInfo.cost) || 0;
                const quantity = log.quantityChange || 0;
                const totalValue = quantity * cost;

                const enrichedLog = {
                    ...log,
                    itemName: itemInfo.name,
                    costUsed: cost,
                    totalValue: totalValue,
                };

                // Add to the new groupedReport
                if (!result.groupedReport[logType]) {
                    result.groupedReport[logType] = [];
                }
                result.groupedReport[logType].push(enrichedLog);

                // --- Update KPIs and Charts ---
                chartData_byTypeCount[log.logType] = (chartData_byTypeCount[log.logType] || 0) + 1;

                if (log.logType === 'DELIVERY') {
                    result.kpis.totalValueIn += totalValue;
                } else if (log.logType === 'SPOILAGE') {
                    result.kpis.totalSpoilage += totalValue;
                } else if (log.logType === 'COUNT_ADJUSTMENT') {
                    result.kpis.totalAdjustments += totalValue;
                }
                // ... handle other log types ...
            });
        }
    });

    result.kpis.totalValueOut = result.kpis.totalSpoilage; // Add other "out" types if needed

    result.charts.pieChartData = Object.keys(chartData_byTypeCount).map(key => ({
        name: key,
        value: chartData_byTypeCount[key],
    }));
    
    // Sort each group's logs by date
    Object.values(result.groupedReport).forEach(group => {
        group.sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));
    });

    return result;
};


// --- Main Report Page Component ---
export const InventoryLogReportPage = forwardRef((props, ref) => {
    const theme = useTheme();
    const [reportData, setReportData] = useState(null);
    const [error, setError] = useState(null);

    // This useEffect runs ONCE when the new tab opens
    useEffect(() => {
        console.log("Report page loaded, attempting to read from sessionStorage...");
        const storedData = sessionStorage.getItem(storageKey);

        if (storedData) {
            try {
                const { items, logs } = JSON.parse(storedData);
                console.log("Data found, processing report...", { items, logs });
                const processed = processLogReportData(items, logs);
                setReportData(processed);
            } catch (err) {
                console.error("Failed to parse report data:", err);
                setError("Failed to load report. Invalid data format.");
            }
        } else {
            console.warn("No report data found in sessionStorage.");
            setError("No report data found. Please generate the report again from the main app.");
        }
    }, []); // Empty dependency array

    // --- Chart Colors ---
    const PIE_COLORS = {
        'DELIVERY': theme.palette.success.main,
        'SPOILAGE': theme.palette.error.main,
        'COUNT_ADJUSTMENT': theme.palette.warning.main,
        'RECIPE_CONSUMPTION': theme.palette.info.main,
        'INTERNAL_USE': theme.palette.grey[500],
    };

    // --- Log Type Display Names and Order ---
    const logTypeDisplayConfig = {
        'DELIVERY': 'Deliveries (Incoming)',
        'RECIPE_CONSUMPTION': 'Recipe Consumption (Sold)',
        'SPOILAGE': 'Spoilage / Waste',
        'COUNT_ADJUSTMENT': 'Count Adjustments',
        'INTERNAL_USE': 'Internal Use',
    };

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 3, my: 3, textAlign: 'center', backgroundColor: 'error.light' }}>
                    <Typography variant="h6" color="error.dark">
                        {error}
                    </Typography>
                </Paper>
            </Container>
        );
    }

    if (!reportData) {
        return (
             <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                    Loading report data...
                </Typography>
            </Container>
        );
    }

    if (reportData.totalLogCount === 0) {
         return (
             <Container maxWidth="xl" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 3, my: 3, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                        No transaction logs found for this period.
                    </Typography>
                </Paper>
            </Container>
        );
    }

    // If you get here, reportData is valid AND has data
    return (
        <Container maxWidth="xl" sx={{ py: 4 }} ref={ref}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                Inventory Transaction Report
            </Typography>

            <Grid container spacing={4} sx={{ mb: 4 }}>
                {/* 1. KPI Section */}
                <Grid item xs={12} md={7}>
                    <Paper elevation={3} sx={{ p: 3, height: '100%', backgroundColor: '#e3f2fd' }}>
                        <Typography variant="h6" sx={{ mb: 3 }}>Key Metrics</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" display="block" color="text.secondary">Total Value In (Delivery)</Typography>
                                <Typography variant="h5" color="success.dark" sx={{ fontWeight: 'bold' }}>
                                    ${reportData.kpis.totalValueIn.toFixed(2)}
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" display="block" color="text.secondary">Total Value Out (Spoilage)</Typography>
                                <Typography variant="h5" color="error.dark" sx={{ fontWeight: 'bold' }}>
                                    ${reportData.kpis.totalValueOut.toFixed(2)}
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" display="block" color="text.secondary">Net Adjustments</Typography>
                                <Typography variant="h5" color={reportData.kpis.totalAdjustments >= 0 ? 'success.dark' : 'error.dark'} sx={{ fontWeight: 'bold' }}>
                                    ${reportData.kpis.totalAdjustments.toFixed(2)}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* 2. Pie Chart Section (Transaction Count) */}
                <Grid item xs={12} md={5}>
                    <Paper elevation={3} sx={{ p: 3, height: '100%', backgroundColor: '#e8f5e9' }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Transactions by Type</Typography>
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={reportData.charts.pieChartData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={100}
                                    fill="#8884d8"
                                    label={(entry) => entry.value}
                                >
                                    {reportData.charts.pieChartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={PIE_COLORS[entry.name] || theme.palette.grey[400]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value, name) => [`${value} logs`, name]} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* --- NEW: Detailed Log Tables Section --- */}
            <Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 5, mb: 3 }}>
                    Detailed Transaction Logs
                </Typography>
                {
                    // Map over the display config object to control the order
                    Object.keys(logTypeDisplayConfig).map(logType => {
                        const logs = reportData.groupedReport[logType];
                        // Only render a table if logs exist for this type
                        if (!logs || logs.length === 0) {
                            return null;
                        }
                        
                        return (
                            <LogTable
                                key={logType}
                                title={logTypeDisplayConfig[logType]}
                                logs={logs}
                                pieColors={PIE_COLORS}
                            />
                        )
                    })
                }
            </Box>

        </Container>
    );
});